// PointsConfiguration's figures (PointsConfigurationModel and its preview script).
import { type PointsRate, type PointsRates } from './api';

/** "N points = 1.00 CUR", with "(+x.x% value)" when cheaper than the default. */
export function ratePreview(points: number, currency: string, defaultPointsPerUnit = 0): string {
  let text = `${points} points = 1.00 ${currency || 'USD'}`;
  if (defaultPointsPerUnit && points > 0 && points < defaultPointsPerUnit)
    text += ` (+${(((defaultPointsPerUnit - points) * 100) / defaultPointsPerUnit).toFixed(1)}% value)`;
  return text;
}

/** "Formula: 1 Point = 0.010000 USD (1 ÷ 100)" */
export const rateFormula = (points: number, currency: string) =>
  `Formula: 1 Point = ${points > 0 ? (1 / points).toFixed(6) : '0.000000'} ${currency || 'USD'} (1 ÷ ${points})`;

const DAY_MS = 86_400_000;

/** Overrides whose expiry (a date, midnight UTC) is within 30 days from now. */
export function expiringSoonCount(overrides: readonly PointsRate[], now: Date): number {
  const limit = now.getTime() + 30 * DAY_MS;
  return overrides.filter((r) => r.expiresOn !== null && Date.parse(`${r.expiresOn}T00:00:00Z`) <= limit).length;
}

/** The override giving the most value against the default, in the default's currency. */
export function highestBonus(rates: PointsRates): { programName: string; bonusPercent: number } | null {
  const base = rates.default.pointsPerUnit;
  if (base <= 0) return null;
  let best: { programName: string; bonusPercent: number } | null = null;
  for (const r of rates.programOverrides) {
    if (r.currency.toLowerCase() !== rates.default.currency.toLowerCase() || r.pointsPerUnit >= base) continue;
    const bonusPercent = ((base - r.pointsPerUnit) * 100) / base;
    if (best === null || bonusPercent > best.bonusPercent)
      best = { programName: r.programName ?? 'Unknown', bonusPercent };
  }
  return best;
}

const csvField = (v: string) => (v.includes(',') || v.includes('"') ? `"${v.replaceAll('"', '""')}"` : v);

/** OnGetExportAsync: qor-points-rates.csv */
export function ratesCsv(rates: PointsRates): string {
  const lines = [
    'Scope,Program,Points Per Unit,Currency,Expires',
    ['Platform Default', '-', rates.default.pointsPerUnit, rates.default.currency, '-'].join(','),
    ...rates.programOverrides.map((r) =>
      [
        'Program Override',
        csvField(r.programName ?? 'Unknown'),
        String(r.pointsPerUnit),
        r.currency,
        r.expiresOn ?? 'No expiry',
      ].join(','),
    ),
  ];
  return `${lines.join('\n')}\n`; // AppendLine on Linux
}

/** A text file for the browser to save. */
export function download(fileName: string, content: string, type = 'text/csv') {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
